import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { Alert, Button, ConfirmDialog, Input, Modal, Select } from '../../../components/ui';

const CATEGORY_OPTIONS = {
    Mainframe: ['MAINFRAME'],
    'Mic Station': ['MIC STATION'],
    IED: ['FA MODULE', 'I/O MODULE'],
    Speaker: ['SPEAKER'],
    Loop: ['LOOP'],
    'Line Array': ['LINE ARRAY'],
    'Noise Sensor': ['NOISE SENSOR'],
    'Digital Audio Bridge': ['DIGITAL AUDIO BRIDGE'],
};

const emptyForm = (deviceType) => ({
    hostname: '',
    ip: '',
    vendor: '',
    vendor_model_name: '',
    MAC: '',
    asset_category: CATEGORY_OPTIONS[deviceType]?.[0] || '',
    location: '',
    building: '',
    floor: '',
    port_switch_number: '',
});

const formFromDevice = (device, deviceType) => ({
    hostname: device?.hostname || '',
    ip: device?.ipAddress || device?.deviceDetails?.ipAddress || '',
    vendor: device?.vendor || '',
    vendor_model_name: device?.vendorPartNumber || device?.deviceDetails?.model || '',
    MAC: device?.macAddress || device?.deviceDetails?.macAddress || '',
    asset_category: device?.inventoryCategory || CATEGORY_OPTIONS[deviceType]?.[0] || '',
    location: device?.assetLocation || '',
    building: device?.building || '',
    floor: device?.floor || '',
    port_switch_number: device?.portSwitchNumber || '',
});

const DETAIL_FIELDS = [
    ['hostname', 'Hostname'],
    ['ip', 'IP address'],
    ['vendor', 'Vendor'],
    ['vendor_model_name', 'Vendor model name'],
    ['MAC', 'MAC address'],
    ['asset_category', 'Asset category'],
    ['location', 'Location'],
    ['building', 'Building'],
    ['floor', 'Floor'],
    ['port_switch_number', 'Port / switch number'],
];

const PasAssetDialog = ({ isOpen, device, deviceType, controller, createMode = false, onClose, onChanged }) => {
    const [editing, setEditing] = useState(createMode);
    const [form, setForm] = useState(emptyForm(deviceType));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setEditing(createMode);
        setForm(createMode ? emptyForm(deviceType) : formFromDevice(device, deviceType));
        setError('');
        setConfirmDelete(false);
    }, [isOpen, createMode, device, deviceType]);

    const categories = useMemo(() => {
        const options = CATEGORY_OPTIONS[deviceType] || [];
        return form.asset_category && !options.includes(form.asset_category)
            ? [form.asset_category, ...options]
            : options;
    }, [deviceType, form.asset_category]);

    const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

    const save = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            if (createMode) {
                await api.post('/api/topology/assets', {
                    ...form,
                    controllerId: controller?._id || controller?.id,
                });
            } else {
                await api.put(`/api/topology/assets/${device._id || device.id}`, form);
            }
            await onChanged?.();
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.message || 'The PAS asset could not be saved.');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        setError('');
        try {
            await api.delete(`/api/topology/assets/${device._id || device.id}`);
            setConfirmDelete(false);
            await onChanged?.();
            onClose();
        } catch (requestError) {
            setConfirmDelete(false);
            setError(requestError.response?.data?.message || 'The PAS asset could not be deleted.');
        } finally {
            setBusy(false);
        }
    };

    const title = createMode ? `Add ${deviceType} asset` : (device?.hostname || device?.name || 'PAS asset');

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                subtitle={controller?.description || controller?.name}
                size="lg"
                footer={
                    editing ? (
                        <>
                            <Button variant="secondary" onClick={createMode ? onClose : () => setEditing(false)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button type="submit" form="pas-asset-form" loading={busy}>Save asset</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="danger-soft" onClick={() => setConfirmDelete(true)} disabled={busy}>Delete</Button>
                            <Button variant="secondary" onClick={onClose}>Close</Button>
                            <Button onClick={() => setEditing(true)}>Edit asset</Button>
                        </>
                    )
                }
            >
                {error && <Alert variant="error" title="Unable to save PAS asset">{error}</Alert>}

                {editing ? (
                    <form id="pas-asset-form" onSubmit={save} className="grid gap-4 sm:grid-cols-2">
                        <Input label="Hostname" value={form.hostname} onChange={change('hostname')} required />
                        <Input label="IP address" value={form.ip} onChange={change('ip')} required />
                        <Input label="Vendor" value={form.vendor} onChange={change('vendor')} />
                        <Input label="Vendor model name" value={form.vendor_model_name} onChange={change('vendor_model_name')} />
                        <Input label="MAC address" value={form.MAC} onChange={change('MAC')} required />
                        <Select label="Asset category" value={form.asset_category} onChange={change('asset_category')} required>
                            {categories.map((category) => <option key={category} value={category}>{category}</option>)}
                        </Select>
                        <Input label="Location" value={form.location} onChange={change('location')} required />
                        <Input label="Building" value={form.building} onChange={change('building')} required />
                        <Input label="Floor" value={form.floor} onChange={change('floor')} />
                        <Input
                            label="Port / switch number"
                            value={form.port_switch_number}
                            onChange={change('port_switch_number')}
                            hint="Example: SW-D-01 / Port 20"
                        />
                    </form>
                ) : (
                    <dl className="grid gap-3 sm:grid-cols-2">
                        {DETAIL_FIELDS.map(([key, label]) => (
                            <div key={key} className="rounded-lg border border-border bg-muted/40 p-3">
                                <dt className="text-xs font-medium uppercase tracking-wide text-muted-fg">{label}</dt>
                                <dd className="mt-1 break-words text-sm text-foreground">{form[key] || '—'}</dd>
                            </div>
                        ))}
                    </dl>
                )}
            </Modal>

            <ConfirmDialog
                isOpen={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={remove}
                title="Delete PAS asset?"
                message={`Delete ${device?.hostname || device?.name || 'this asset'} and its device details? This cannot be undone.`}
                confirmLabel="Delete asset"
                loading={busy}
            />
        </>
    );
};

export default PasAssetDialog;
